import { useState, useEffect, useCallback } from 'react';
import { PageLayout } from '@/components/PageLayout';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Plus, CalendarDays, X, Check, ChevronLeft, ChevronRight, Clock,
  ThumbsUp, ThumbsDown, UserX, UserPlus, CalendarRange, List,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { AppointmentCalendar, StatusLegend } from '@/components/appointments/AppointmentCalendar';
import { isValidBusinessEmail, EMAIL_ERROR_TR } from '@/lib/email';
import { useSectorTerms } from '@/utils/useSectorTerms';

// Canonical status → label/colour. Aliases (confirmed→scheduled, pending→pending_approval)
// are normalised server-side at read time but we tolerate them here too.
const STATUS_LABELS = {
  scheduled:        { text: 'Onaylandı',    color: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  pending_approval: { text: 'Onay Bekliyor', color: 'bg-blue-100 text-blue-800 border-blue-200' },
  completed:        { text: 'Tamamlandı',   color: 'bg-gray-100 text-gray-700 border-gray-200' },
  cancelled:        { text: 'İptal Edildi', color: 'bg-red-100 text-red-700 border-red-200' },
  rejected:         { text: 'Reddedildi',   color: 'bg-red-100 text-red-700 border-red-200' },
  no_show:          { text: 'Gelmedi',      color: 'bg-amber-100 text-amber-800 border-amber-200' },
  // legacy fallbacks
  confirmed:        { text: 'Onaylandı',    color: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  pending:          { text: 'Onay Bekliyor', color: 'bg-blue-100 text-blue-800 border-blue-200' },
};

const PAYMENT_STATUS = {
  unpaid:         { text: 'Ödeme alınmadı', color: 'bg-red-50 text-red-700 border-red-200' },
  paid:           { text: 'Ödeme alındı', color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  after_service: { text: 'İşlem sonrası tahsil edilecek', color: 'bg-gray-50 text-gray-600 border-gray-200' },
};

const addMinutes = (timeStr, minutes) => {
  if (!timeStr) return timeStr;
  const [h, m] = timeStr.split(':').map(Number);
  const total = h * 60 + m + (minutes || 0);
  return `${String(Math.floor(total / 60) % 24).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
};

const todayStr = () => new Date().toISOString().split('T')[0];

export const AdminAppointments = () => {
  const { user: currentUser } = useAuth();
  const API_URL = process.env.REACT_APP_API_URL;
  const labels = currentUser?.labels || { people: 'Müşteriler' };
  const terms = useSectorTerms();

  const [appointments, setAppointments] = useState([]);
  const [users, setUsers] = useState([]);
  const [staff, setStaff] = useState([]);
  const [branches, setBranches] = useState([]);
  const [services, setServices] = useState([]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [quickCustomerOpen, setQuickCustomerOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(todayStr());
  const [calView, setCalView] = useState('list'); // 'list' | 'day' | 'week'
  const [staffFilter, setStaffFilter] = useState('');

  const [form, setForm] = useState({
    client_id: '', service: '', date: todayStr(),
    start_time: '09:00', end_time: '09:30',
    price: '', notes: '', branch_id: '', staff_id: '',
    payment_status: 'after_service',
  });

  const [newCustomer, setNewCustomer] = useState({ name: '', phone: '', email: '', notes: '' });
  const [quickStaffOpen, setQuickStaffOpen] = useState(false);
  const [newStaff, setNewStaff] = useState({ name: '', phone: '' });

  const fetchUsers = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/users`, { withCredentials: true });
      setUsers(data || []);
    } catch { /* ignore */ }
  }, [API_URL]);

  const fetchStaff = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/staff`, { withCredentials: true });
      setStaff(data || []);
    } catch { /* ignore */ }
  }, [API_URL]);

  const fetchBranches = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/branches`, { withCredentials: true });
      setBranches(data?.items || data || []);
    } catch { /* ignore */ }
  }, [API_URL]);

  const fetchServices = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/appointments/services`, { withCredentials: true });
      setServices(data.services || []);
    } catch { /* ignore */ }
  }, [API_URL]);

  const fetchAppointments = useCallback(async () => {
    try {
      // For calendar views we pull a wider window so navigation is smooth.
      const d = new Date(selectedDate + 'T00:00:00');
      const from = new Date(d); from.setDate(d.getDate() - 14);
      const to = new Date(d); to.setDate(d.getDate() + 30);
      const fromStr = from.toISOString().split('T')[0];
      const toStr = to.toISOString().split('T')[0];
      const url = `${API_URL}/api/appointments?date_from=${fromStr}&date_to=${toStr}`;
      const { data } = await axios.get(url, { withCredentials: true });
      setAppointments(Array.isArray(data) ? data : []);
    } catch { /* ignore */ }
  }, [API_URL, selectedDate]);

  useEffect(() => { fetchUsers(); fetchStaff(); fetchBranches(); fetchServices(); }, [fetchUsers, fetchStaff, fetchBranches, fetchServices]);
  useEffect(() => { fetchAppointments(); }, [fetchAppointments]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.client_id) return toast.error(`${terms.customerLabelSingular} seçin`);
    if (!form.service) return toast.error('Hizmet seçin');
    try {
      await axios.post(`${API_URL}/api/appointments`, {
        ...form, price: form.price ? Number(form.price) : null,
        branch_id: form.branch_id || null,
        staff_id: form.staff_id || null,
      }, { withCredentials: true });
      toast.success('Randevu oluşturuldu');
      setDialogOpen(false);
      setForm({ ...form, client_id: '', service: '', notes: '', price: '' });
      fetchAppointments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Randevu oluşturulamadı');
    }
  };

  const handleQuickCustomer = async (e) => {
    e.preventDefault();
    if (!newCustomer.name.trim() || !newCustomer.phone.trim()) {
      return toast.error('Ad ve telefon zorunludur');
    }
    if (newCustomer.email && !isValidBusinessEmail(newCustomer.email)) {
      return toast.error(EMAIL_ERROR_TR);
    }
    try {
      const { data } = await axios.post(`${API_URL}/api/appointments/quick-customer`,
        newCustomer, { withCredentials: true });
      toast.success(`${data.name} eklendi`);
      // refresh user list & auto-select
      await fetchUsers();
      setForm((f) => ({ ...f, client_id: data.id }));
      setNewCustomer({ name: '', phone: '', email: '', notes: '' });
      setQuickCustomerOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || `${terms.customerLabelSingular} eklenemedi`);
    }
  };

  const handleQuickStaff = async (e) => {
    e.preventDefault();
    if (!newStaff.name.trim()) return toast.error('Personel adı zorunludur');
    try {
      const { data } = await axios.post(`${API_URL}/api/staff/quick`,
        newStaff, { withCredentials: true });
      toast.success(`${data.name} personeli eklendi`);
      await fetchStaff();
      setForm((f) => ({ ...f, staff_id: data.id }));
      setNewStaff({ name: '', phone: '' });
      setQuickStaffOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Personel eklenemedi');
    }
  };

  const callStatusEndpoint = async (id, action, successMsg) => {
    try {
      await axios.put(`${API_URL}/api/appointments/${id}/${action}`, {}, { withCredentials: true });
      toast.success(successMsg);
      fetchAppointments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'İşlem başarısız');
    }
  };

  const handleApprove   = (id) => callStatusEndpoint(id, 'approve',   'Randevu onaylandı');
  const handleReject    = (id) => callStatusEndpoint(id, 'reject',    'Randevu reddedildi');
  const handleComplete  = (id) => callStatusEndpoint(id, 'complete',  'Randevu tamamlandı');
  const handleNoShow    = (id) => callStatusEndpoint(id, 'no-show',   '"Gelmedi" olarak işaretlendi');
  const handleCancel    = async (id) => {
    if (!window.confirm('Randevuyu iptal etmek istediğinizden emin misiniz?')) return;
    callStatusEndpoint(id, 'cancel', 'Randevu iptal edildi');
  };

  const updatePaymentStatus = async (id, status) => {
    try {
      await axios.put(`${API_URL}/api/appointments/${id}/payment-status`,
        { payment_status: status }, { withCredentials: true });
      toast.success('Ödeme durumu güncellendi');
      fetchAppointments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Güncellenemedi');
    }
  };

  const changeDate = (days) => {
    const d = new Date(selectedDate + 'T00:00:00');
    d.setDate(d.getDate() + days);
    setSelectedDate(d.toISOString().split('T')[0]);
  };

  // Lists filtered for the active tab
  const filteredApts = staffFilter
    ? appointments.filter((a) => a.staff_id === staffFilter)
    : appointments;
  const todayList = filteredApts.filter((a) => a.date === selectedDate);
  const pending = filteredApts.filter((a) => a.status === 'pending_approval' || a.status === 'pending');
  const todayCount = filteredApts.filter((a) => a.date === todayStr() && (a.status === 'scheduled' || a.status === 'confirmed')).length;
  const totalRevenue = filteredApts
    .filter((a) => a.status === 'completed' && a.price)
    .reduce((sum, a) => sum + Number(a.price || 0), 0);

  return (
    <PageLayout>
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-2">
          <div>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#0F172A]" data-testid="appointments-page-title">
              Randevular
            </h1>
            <p className="text-sm text-[#595959] mt-1">Online randevu sisteminizi yönetin</p>
          </div>
          <Button
            onClick={() => { setForm((f) => ({ ...f, date: selectedDate })); setDialogOpen(true); }}
            className="bg-[#2563EB] hover:bg-[#1E4ED8] text-white"
            data-testid="add-appointment-button"
          >
            <Plus className="w-5 h-5 mr-2" /> Randevu Oluştur
          </Button>
        </div>

        {/* ----- Toolbar: view switch + date nav + staff filter ----- */}
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4 bg-white border border-[#E5E5E5] rounded-lg p-3">
          <div className="flex items-center gap-1" data-testid="view-switch">
            <Button size="sm" variant={calView === 'list' ? 'default' : 'outline'} onClick={() => setCalView('list')} className={calView === 'list' ? 'bg-[#2563EB] text-white' : ''} data-testid="view-list-btn"><List className="w-4 h-4 mr-1" />Liste</Button>
            <Button size="sm" variant={calView === 'day' ? 'default' : 'outline'} onClick={() => setCalView('day')} className={calView === 'day' ? 'bg-[#2563EB] text-white' : ''} data-testid="view-day-btn"><CalendarDays className="w-4 h-4 mr-1" />Günlük</Button>
            <Button size="sm" variant={calView === 'week' ? 'default' : 'outline'} onClick={() => setCalView('week')} className={calView === 'week' ? 'bg-[#2563EB] text-white' : ''} data-testid="view-week-btn"><CalendarRange className="w-4 h-4 mr-1" />Haftalık</Button>
          </div>

          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => changeDate(calView === 'week' ? -7 : -1)} data-testid="prev-day-button"><ChevronLeft className="w-4 h-4" /></Button>
            <Input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} className="w-44 text-center" data-testid="date-filter-input" />
            <Button variant="ghost" size="sm" onClick={() => changeDate(calView === 'week' ? 7 : 1)} data-testid="next-day-button"><ChevronRight className="w-4 h-4" /></Button>
            <Button variant="outline" size="sm" onClick={() => setSelectedDate(todayStr())} data-testid="today-btn">Bugün</Button>
          </div>

          {staff.length > 0 && (
            <div className="flex items-center gap-2">
              <Label className="text-xs text-[#595959]">Personel:</Label>
              <Select value={staffFilter || 'all'} onValueChange={(v) => setStaffFilter(v === 'all' ? '' : v)}>
                <SelectTrigger className="w-44" data-testid="staff-filter"><SelectValue placeholder="Tümü" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tümü</SelectItem>
                  {staff.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        {/* ----- Stats ----- */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
          <Card className="border border-[#E5E5E5]"><CardContent className="p-3 text-center"><p className="text-2xl font-bold text-[#0F172A]" data-testid="today-count">{todayCount}</p><p className="text-xs text-[#595959]">Bugünkü Onaylı</p></CardContent></Card>
          <Card className="border border-[#E5E5E5]"><CardContent className="p-3 text-center"><p className="text-2xl font-bold text-[#0F172A]" data-testid="total-appointments">{filteredApts.length}</p><p className="text-xs text-[#595959]">Toplam (30 gün)</p></CardContent></Card>
          <Card className="border border-[#E5E5E5]"><CardContent className="p-3 text-center"><p className="text-2xl font-bold text-blue-700" data-testid="pending-count">{pending.length}</p><p className="text-xs text-[#595959]">Onay Bekleyen</p></CardContent></Card>
          <Card className="border border-[#E5E5E5]"><CardContent className="p-3 text-center"><p className="text-2xl font-bold text-emerald-700" data-testid="total-revenue">{totalRevenue.toLocaleString('tr-TR')} ₺</p><p className="text-xs text-[#595959]">Tamamlanan</p></CardContent></Card>
        </div>

        {/* ----- View body ----- */}
        {calView === 'list' && (
          <Tabs defaultValue="all" className="space-y-4">
            <TabsList className="grid w-full grid-cols-2 h-11" data-testid="admin-appointment-tabs">
              <TabsTrigger value="all" className="gap-2 text-sm"><CalendarDays className="w-4 h-4" />Tüm Randevular</TabsTrigger>
              <TabsTrigger value="pending" className="gap-2 text-sm" data-testid="tab-pending"><Clock className="w-4 h-4" />Onay Bekleyen {pending.length > 0 && <span className="bg-blue-500 text-white text-xs rounded-full px-1.5">{pending.length}</span>}</TabsTrigger>
            </TabsList>

            <TabsContent value="all">
              <AppointmentTable
                appointments={filteredApts}
                labels={labels}
                terms={terms}
                onApprove={handleApprove}
                onReject={handleReject}
                onComplete={handleComplete}
                onCancel={handleCancel}
                onNoShow={handleNoShow}
                onPaymentChange={updatePaymentStatus}
              />
            </TabsContent>
            <TabsContent value="pending">
              <AppointmentTable
                appointments={pending}
                labels={labels}
                terms={terms}
                onApprove={handleApprove}
                onReject={handleReject}
                onComplete={handleComplete}
                onCancel={handleCancel}
                onNoShow={handleNoShow}
                onPaymentChange={updatePaymentStatus}
              />
            </TabsContent>
          </Tabs>
        )}

        {(calView === 'day' || calView === 'week') && (
          <div>
            <AppointmentCalendar
              view={calView}
              anchorDate={selectedDate}
              appointments={filteredApts}
              staff={calView === 'day' ? (staffFilter ? staff.filter((s) => s.id === staffFilter) : staff) : []}
            />
            <StatusLegend />
            {calView === 'day' && todayList.length === 0 && (
              <p className="text-center text-[#999] mt-6 text-sm">{selectedDate} tarihinde randevu yok.</p>
            )}
          </div>
        )}
      </div>

      {/* ===== Create Modal ===== */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="appointment-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Yeni Randevu</DialogTitle>
            <DialogDescription>Hizmet veren işletmenize uygun randevu oluşturun.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
            {/* Customer + quick add */}
            <div>
              <Label>{terms.customerLabelSingular}</Label>
              {users.length > 0 ? (
                <div className="flex gap-2">
                  <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                    <SelectTrigger data-testid="appointment-client-select" className="flex-1"><SelectValue placeholder={`${terms.customerLabelSingular} seçin`} /></SelectTrigger>
                    <SelectContent>
                      {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} {u.phone && <span className="text-xs text-[#999] ml-1">· {u.phone}</span>}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button type="button" variant="outline" onClick={() => setQuickCustomerOpen(true)} className="shrink-0 gap-1" data-testid="quick-customer-btn">
                    <UserPlus className="w-4 h-4" /> Yeni
                  </Button>
                </div>
              ) : (
                <div
                  className="border border-dashed border-blue-300 bg-blue-50 rounded-md p-3 flex items-start gap-3"
                  data-testid="customer-empty-hint"
                >
                  <div className="flex-1 text-xs text-blue-900 leading-relaxed">
                    Henüz {terms.customerLabelPlural.toLocaleLowerCase('tr-TR')} yok. Hemen ekleyip ilk randevunuzu oluşturun.
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => setQuickCustomerOpen(true)}
                    className="bg-[#2563EB] hover:bg-[#1E4ED8] text-white shrink-0 gap-1"
                    data-testid="customer-empty-add-btn"
                  >
                    <UserPlus className="w-4 h-4" /> {terms.addCustomerLabel}
                  </Button>
                </div>
              )}
            </div>

            {/* Service */}
            <div>
              <Label>Hizmet</Label>
              {services.length > 0 ? (
                <Select
                  value={form.service}
                  onValueChange={(v) => {
                    const svc = services.find((s) => s.name === v);
                    const hasDur = svc?.duration_min && svc.duration_min > 0;
                    const hasPrice = svc?.default_price && svc.default_price > 0;
                    setForm({
                      ...form,
                      service: v,
                      price: hasPrice ? String(svc.default_price) : form.price,
                      end_time: hasDur ? addMinutes(form.start_time, svc.duration_min) : form.end_time,
                    });
                  }}
                >
                  <SelectTrigger data-testid="appointment-service-select"><SelectValue placeholder="Hizmet seçin" /></SelectTrigger>
                  <SelectContent>
                    {services.map((s) => {
                      const hasDur = s.duration_min && s.duration_min > 0;
                      const hasPrice = s.default_price && s.default_price > 0;
                      const meta = [
                        hasDur ? `${s.duration_min} dk` : null,
                        hasPrice ? `${s.default_price} ₺` : null,
                      ].filter(Boolean).join(' · ');
                      return (
                        <SelectItem key={s.name} value={s.name}>
                          {s.name}{meta && <span className="text-xs text-[#999] ml-2">· {meta}</span>}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              ) : (
                <Input value={form.service} onChange={(e) => setForm({ ...form, service: e.target.value })} placeholder="Örn: Saç kesimi" required data-testid="appointment-service-input" />
              )}
              {services.length === 0 && (
                <p className="text-xs text-amber-700 mt-1">Hizmet listesi gelmedi — Ayarlar &rarr; Randevu Kategorisi seçmiş olduğunuza emin olun.</p>
              )}
            </div>

            {/* Staff — always visible to advertise the feature */}
            <div>
              <Label>Personel</Label>
              {staff.length > 0 ? (
                <div className="flex gap-2">
                  <Select value={form.staff_id || 'none'} onValueChange={(v) => setForm({ ...form, staff_id: v === 'none' ? '' : v })}>
                    <SelectTrigger data-testid="appointment-staff-select" className="flex-1"><SelectValue placeholder="Personel seçin" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Atanmadı</SelectItem>
                      {staff.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button type="button" variant="outline" onClick={() => setQuickStaffOpen(true)} className="shrink-0 gap-1" data-testid="quick-staff-btn">
                    <UserPlus className="w-4 h-4" /> Yeni
                  </Button>
                </div>
              ) : (
                <div
                  className="border border-dashed border-amber-300 bg-amber-50 rounded-md p-3 flex items-start gap-3"
                  data-testid="staff-empty-hint"
                >
                  <div className="flex-1 text-xs text-amber-900 leading-relaxed">
                    Henüz personel eklenmemiş. Personel bazlı randevu takibi için önce personel ekleyebilirsiniz.
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => setQuickStaffOpen(true)}
                    className="bg-amber-600 hover:bg-amber-700 text-white shrink-0 gap-1"
                    data-testid="staff-empty-add-btn"
                  >
                    <UserPlus className="w-4 h-4" /> Personel Ekle
                  </Button>
                </div>
              )}
            </div>

            {/* Branch */}
            {branches.length > 0 && (
              <div>
                <Label>Şube</Label>
                <Select value={form.branch_id || 'none'} onValueChange={(v) => setForm({ ...form, branch_id: v === 'none' ? '' : v })}>
                  <SelectTrigger data-testid="appointment-branch-select"><SelectValue placeholder="Şube" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Şube seçilmedi</SelectItem>
                    {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="grid grid-cols-3 gap-3">
              <div><Label>Tarih</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required data-testid="appointment-date-input" /></div>
              <div><Label>Başlangıç</Label><Input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} required data-testid="appointment-start-input" /></div>
              <div><Label>Bitiş</Label><Input type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} required data-testid="appointment-end-input" /></div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div><Label>Ücret (₺)</Label><Input type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} placeholder="0.00" data-testid="appointment-price-input" /></div>
              <div>
                <Label>Ödeme Durumu</Label>
                <Select value={form.payment_status} onValueChange={(v) => setForm({ ...form, payment_status: v })}>
                  <SelectTrigger data-testid="appointment-payment-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="after_service">İşlem sonrası tahsil</SelectItem>
                    <SelectItem value="paid">Ödeme alındı</SelectItem>
                    <SelectItem value="unpaid">Ödeme alınmadı</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div><Label>Not</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} data-testid="appointment-notes-input" /></div>

            <Button type="submit" className="w-full bg-[#2563EB] hover:bg-[#1E4ED8] text-white" data-testid="appointment-submit-button">Randevu Oluştur</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* ===== Quick-add Customer Modal ===== */}
      <Dialog open={quickCustomerOpen} onOpenChange={setQuickCustomerOpen}>
        <DialogContent data-testid="quick-customer-modal" className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Yeni {terms.customerLabelSingular} Ekle</DialogTitle>
            <DialogDescription>Hızlıca kaydedip randevuya atayın.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleQuickCustomer} className="space-y-3">
            <div><Label>Ad Soyad *</Label><Input value={newCustomer.name} onChange={(e) => setNewCustomer({ ...newCustomer, name: e.target.value })} required data-testid="qc-name" /></div>
            <div><Label>Telefon *</Label><Input value={newCustomer.phone} onChange={(e) => setNewCustomer({ ...newCustomer, phone: e.target.value })} placeholder="0555 111 22 33" required data-testid="qc-phone" /></div>
            <div><Label>E-posta (opsiyonel)</Label><Input type="email" value={newCustomer.email} onChange={(e) => setNewCustomer({ ...newCustomer, email: e.target.value })} data-testid="qc-email" /></div>
            <div><Label>Not (opsiyonel)</Label><Textarea rows={2} value={newCustomer.notes} onChange={(e) => setNewCustomer({ ...newCustomer, notes: e.target.value })} data-testid="qc-notes" /></div>
            <Button type="submit" className="w-full bg-[#2563EB] hover:bg-[#1E4ED8] text-white" data-testid="qc-submit">Kaydet</Button>
          </form>
        </DialogContent>
      </Dialog>
      {/* ===== Quick-add Staff Modal ===== */}
      <Dialog open={quickStaffOpen} onOpenChange={setQuickStaffOpen}>
        <DialogContent data-testid="quick-staff-modal" className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Yeni Personel Ekle</DialogTitle>
            <DialogDescription>Randevu takibi için hızlı kayıt. Personel daha sonra "şifre sıfırla" ile giriş yapabilir.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleQuickStaff} className="space-y-3">
            <div><Label>Ad Soyad *</Label><Input value={newStaff.name} onChange={(e) => setNewStaff({ ...newStaff, name: e.target.value })} required data-testid="qs-name" /></div>
            <div><Label>Telefon (opsiyonel)</Label><Input value={newStaff.phone} onChange={(e) => setNewStaff({ ...newStaff, phone: e.target.value })} placeholder="0555 111 22 33" data-testid="qs-phone" /></div>
            <Button type="submit" className="w-full bg-[#2563EB] hover:bg-[#1E4ED8] text-white" data-testid="qs-submit">Kaydet</Button>
          </form>
        </DialogContent>
      </Dialog>
    </PageLayout>
  );
};

// ---------- Table (desktop) + Cards (mobile) ----------
const AppointmentTable = ({
  appointments, labels, terms, onApprove, onReject, onComplete, onCancel, onNoShow, onPaymentChange,
}) => (
  <>
    {/* MOBILE: card grid (<md) */}
    <div className="md:hidden space-y-3" data-testid="appointments-mobile-cards">
      {appointments.length === 0 ? (
        <div className="text-center py-8 text-[#595959] bg-white border border-[#E5E5E5] rounded-xl">
          <CalendarDays className="w-8 h-8 mx-auto mb-2 opacity-50" />Randevu bulunamadı
        </div>
      ) : appointments.map((apt) => {
        const status = STATUS_LABELS[apt.status] || STATUS_LABELS.scheduled;
        const pay = PAYMENT_STATUS[apt.payment_status || 'after_service'];
        const isPending = apt.status === 'pending_approval' || apt.status === 'pending';
        const isScheduled = apt.status === 'scheduled' || apt.status === 'confirmed';
        return (
          <div key={apt.id} className="bg-white border border-[#E5E5E5] rounded-xl p-3 shadow-sm" data-testid={`appointment-card-${apt.id}`}>
            <div className="flex justify-between items-start mb-2">
              <div>
                <div className="font-semibold text-[#0F172A]">{apt.client_name}</div>
                {apt.client_phone && <div className="text-xs text-[#999]">{apt.client_phone}</div>}
              </div>
              <span className={`px-2 py-1 rounded-full text-[10px] font-medium border ${status.color}`}>{status.text}</span>
            </div>
            <div className="text-sm text-[#1A1A2E] flex items-center gap-2 mb-1.5">
              <Clock className="w-3.5 h-3.5 text-[#595959]" />
              <span>{apt.date} · {apt.start_time}-{apt.end_time}</span>
            </div>
            <div className="text-sm text-[#1A1A2E] mb-2">
              <span className="font-medium">{apt.service}</span>
              {apt.staff_name && <span className="text-xs text-[#595959]"> · {apt.staff_name}</span>}
              {apt.price ? <span className="text-xs text-[#595959]"> · {apt.price} ₺</span> : null}
            </div>
            <div className="flex items-center justify-between gap-2 pt-2 border-t border-[#F3F4F6]">
              <span className={`text-[10px] px-2 py-1 rounded border ${pay.color}`}>{pay.text}</span>
              <div className="flex gap-1">
                {isPending && (
                  <>
                    <Button size="sm" variant="ghost" onClick={() => onApprove(apt.id)} className="h-8 px-2 gap-1" data-testid={`m-approve-${apt.id}`}><ThumbsUp className="w-4 h-4 text-emerald-600" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => onReject(apt.id)} className="h-8 px-2" data-testid={`m-reject-${apt.id}`}><ThumbsDown className="w-4 h-4 text-red-500" /></Button>
                  </>
                )}
                {isScheduled && (
                  <>
                    <Button size="sm" variant="ghost" onClick={() => onComplete(apt.id)} className="h-8 px-2" data-testid={`m-complete-${apt.id}`}><Check className="w-4 h-4 text-emerald-600" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => onNoShow(apt.id)} className="h-8 px-2" data-testid={`m-noshow-${apt.id}`}><UserX className="w-4 h-4 text-amber-600" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => onCancel(apt.id)} className="h-8 px-2" data-testid={`m-cancel-${apt.id}`}><X className="w-4 h-4 text-red-500" /></Button>
                  </>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>

    {/* DESKTOP: table (md+) */}
    <div className="hidden md:block bg-white border border-[#E5E5E5] rounded-xl overflow-x-auto" data-testid="appointments-table">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Tarih</TableHead><TableHead>Saat</TableHead>
            <TableHead>{terms.customerLabelSingular}</TableHead>
            <TableHead>Hizmet</TableHead>
            <TableHead className="hidden md:table-cell">Personel</TableHead>
            <TableHead>Ücret</TableHead>
            <TableHead>Durum</TableHead>
            <TableHead className="hidden md:table-cell">Ödeme</TableHead>
            <TableHead className="text-right">İşlemler</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {appointments.length === 0 ? (
            <TableRow><TableCell colSpan={9} className="text-center py-8 text-[#595959]"><CalendarDays className="w-8 h-8 mx-auto mb-2 opacity-50" />Randevu bulunamadı</TableCell></TableRow>
          ) : appointments.map((apt) => {
            const status = STATUS_LABELS[apt.status] || STATUS_LABELS.scheduled;
            const pay = PAYMENT_STATUS[apt.payment_status || 'after_service'];
            const isPending = apt.status === 'pending_approval' || apt.status === 'pending';
            const isScheduled = apt.status === 'scheduled' || apt.status === 'confirmed';
            return (
              <TableRow key={apt.id} data-testid={`appointment-row-${apt.id}`}>
                <TableCell className="font-medium whitespace-nowrap">{apt.date}</TableCell>
                <TableCell className="whitespace-nowrap">{apt.start_time}-{apt.end_time}</TableCell>
                <TableCell>
                  <div>{apt.client_name}</div>
                  {apt.client_phone && <div className="text-xs text-[#999]">{apt.client_phone}</div>}
                  {apt.branch_name && <div className="text-xs text-[#999]">{apt.branch_name}</div>}
                </TableCell>
                <TableCell>{apt.service}</TableCell>
                <TableCell className="hidden md:table-cell text-sm">{apt.staff_name || '-'}</TableCell>
                <TableCell>{apt.price ? `${apt.price} ₺` : '-'}</TableCell>
                <TableCell><span className={`px-2 py-1 rounded-full text-xs font-medium border ${status.color}`} data-testid={`status-badge-${apt.id}`}>{status.text}</span></TableCell>
                <TableCell className="hidden md:table-cell">
                  <Select value={apt.payment_status || 'after_service'} onValueChange={(v) => onPaymentChange(apt.id, v)}>
                    <SelectTrigger className={`h-8 text-xs ${pay.color} border`} data-testid={`pay-select-${apt.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="after_service">İşlem sonrası</SelectItem>
                      <SelectItem value="paid">Ödendi</SelectItem>
                      <SelectItem value="unpaid">Ödenmedi</SelectItem>
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    {isPending && (
                      <>
                        <Button variant="ghost" size="sm" onClick={() => onApprove(apt.id)} title="Onayla" data-testid={`approve-apt-${apt.id}`}><ThumbsUp className="w-4 h-4 text-emerald-600" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => onReject(apt.id)} title="Reddet" data-testid={`reject-apt-${apt.id}`}><ThumbsDown className="w-4 h-4 text-red-500" /></Button>
                      </>
                    )}
                    {isScheduled && (
                      <>
                        <Button variant="ghost" size="sm" onClick={() => onComplete(apt.id)} title="Tamamla" data-testid={`complete-apt-${apt.id}`}><Check className="w-4 h-4 text-emerald-600" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => onNoShow(apt.id)} title="Gelmedi" data-testid={`noshow-apt-${apt.id}`}><UserX className="w-4 h-4 text-amber-600" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => onCancel(apt.id)} title="İptal" data-testid={`cancel-apt-${apt.id}`}><X className="w-4 h-4 text-red-500" /></Button>
                      </>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  </>
);
